import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";

import { Skeleton } from "antd";
import moment from "moment";

import orderApi from "../../../apis/Order.api";
import partnersApi from "../../../apis/Partners.api";
import summaryApi from "../../../apis/Summary.api";
import ApiService from "../../../utils/ApiService";
import { historyList } from "../../../utils/status";

import AgeChart from "./channel/AgeChart";
import GenderChart from "./channel/GenderChart";

const api = new ApiService();

const DashboardChannel = () => {
	const user = useSelector((state) => state.auth.user);
	const [loading, setLoading] = useState(false);

	const [userCount, setUserCount] = useState({
		now: 0,
		last_week: 0,
		last_month: 0,
	});

	const [genderCount, setGenderCount] = useState({
		female: 0,
		male: 0,
		unknown: 0,
	});

	const [ageList, setAgeList] = useState([]);

	useEffect(() => {
		if (user?.id) {
			getUserCount();
			getUserThatTime();
		}
		return () => {
			setLoading(false);
		};
	}, [user]);

	/**
	 * 유저 정보 가져오기
	 * 오늘/30일 이전
	 * 마지막 시간 기준 (23:59:59)
	 * @type {(function(): Promise<void>)|*}
	 */
	const getUserCount = useCallback(async () => {
		setLoading(true);
		const param = {
			endDate: moment().endOf("days").format("YYYYMMDDHHmm"),
			userId:
				user?.role === "level_1" || user?.role === "super_admin"
					? user?.id
					: user?.partners_id,
		};
		const week = {
			endDate: moment()
				.subtract(7, "days")
				.endOf("days")
				.format("YYYYMMDDHHmm"),
			userId:
				user?.role === "level_1" || user?.role === "super_admin"
					? user?.id
					: user?.partners_id,
		};
		const month = {
			endDate: moment()
				.subtract(30, "days")
				.endOf("days")
				.format("YYYYMMDDHHmm"),
			userId:
				user?.role === "level_1" || user?.role === "super_admin"
					? user?.id
					: user?.partners_id,
		};
		const res = await partnersApi.readSignUpUserCount(param);
		const resW = await partnersApi.readSignUpUserCount(week);
		const resM = await partnersApi.readSignUpUserCount(month);
		if (res.code === 200 && resW.code === 200 && resM.code === 200) {
			setUserCount({
				now: res.result?.userCount,
				last_week: resW.result?.userCount,
				last_month: resM.result?.userCount,
			});
		} else {
			setUserCount({
				now: 0,
				last_week: 0,
				last_month: 0,
			});
		}
		if (res.code === 200) {
			setGenderCount({
				female: res.result?.femaleCount,
				male: res.result?.maleCount,
				unknown: res.result?.unknownGenderCount,
			});
			setAgeList(res.result?.userSummaryList);
		} else {
			setGenderCount({
				female: 0,
				male: 0,
				unknown: 0,
			});
			setAgeList([]);
		}
		setLoading(false);
	}, [user]);

	const [userStreamingList, setUserStreamingList] = useState([]);
	const [todayValue, setTodayValue] = useState({
		inflow: 0,
		signup: 0,
	});
	const [purchaseCount, setPurchaseCount] = useState(0);

	const getUserThatTime = async () => {
		setLoading(true);
		const param = {
			userId:
				user?.role === "level_1" || user?.role === "super_admin"
					? user?.id
					: user?.partners_id,
			startDate: moment().subtract(60, "minutes").format("YYYYMMDDHHmm"),
			endDate: moment().format("YYYYMMDDHHmm"),
		};
		const todayParam = {
			userId:
				user?.role === "level_1" || user?.role === "super_admin"
					? user?.id
					: user?.partners_id,
			startDate: moment().startOf("days").format("YYYYMMDDHHmm"),
			endDate: moment().endOf("days").format("YYYYMMDDHHmm"),
		};
		const res = await summaryApi.readDashboardUserThatTime(param);
		const rsp = await summaryApi.readDashboardUserThatTime(todayParam);
		if (res.code === 200 && rsp.code === 200) {
			setUserStreamingList(historyList(res.result));
			let list = rsp.result;
			let todayInflow = list.filter((item) => item.action === "first_inflow");
			let todaySignup = list.filter((item) => item.action === "signup");
			setTodayValue({
				inflow: todayInflow?.length,
				signup: todaySignup?.length,
			});
			getCustomerBuying();
		}
		setLoading(false);
	};

	const [purchaseLoading, setPurchaseLoading] = useState(false);

	const getCustomerBuying = async () => {
		setPurchaseLoading(true);
		const linkParam = {
			userId: user?.id,
		};
		const linkRes = await api.getProductLinkList(linkParam);
		if (linkRes.data.code === 200 && linkRes.data.result) {
			const linkList = linkRes.data.result?.map((link) => link.query);
			const query = linkList.join(",");
			const param = {
				startDate: moment().subtract(60, "minutes").format("YYYYMMDDHHmm"),
				endDate: moment().format("YYYYMMDDHHmm"),
				queryList: query,
				orderType: "created_at",
				orderBy: "desc",
			};
			const res = await orderApi.orderByInfluencer(param);
			if (res.code === 200) {
				setPurchaseCount(res.result?.totalCnt);
			}
		}
		setPurchaseLoading(false);
	};

	return (
		<div className={`dashboard-body-each`}>
			<h2 className={`dashboard-each-title`}>채널 분석</h2>
			<div className={`dashboard-huge-number-div`}>
				<Skeleton
					loading={loading}
					active={loading}
					size={"large"}
					block={true}
					style={{
						height: "6em",
					}}
					paragraph={{
						rows: 3,
						width: "100%",
					}}
				>
					<p>현재 가입자 수</p>
					<div style={{ display: "flex", alignItems: "end", gap: "2px" }}>
						<h3>{userCount?.now?.toLocaleString()}</h3>
					</div>
					<div
						className={`compare-price-div`}
						style={{ flexDirection: "row", gap: 5 }}
					>
						<p className={`gray`}>지난 7일 동안</p>
						{userCount?.now - userCount?.last_week > 0 ? (
							<p className={`orange`}>
								{(userCount?.now - userCount?.last_week).toLocaleString()}명
								증가
							</p>
						) : (
							<p className={`orange`}>
								{Math.abs(
									userCount?.now - userCount?.last_week
								).toLocaleString()}
								명 감소
							</p>
						)}
					</div>
					<div
						className={`compare-price-div`}
						style={{ flexDirection: "row", gap: 5 }}
					>
						<p className={`gray`}>지난 30일 동안</p>
						{userCount?.now - userCount?.last_month > 0 ? (
							<p className={`orange`}>
								{(userCount?.now - userCount?.last_month).toLocaleString()}명
								증가
							</p>
						) : (
							<p className={`orange`}>
								{Math.abs(
									userCount?.now - userCount?.last_month
								).toLocaleString()}
								명 감소
							</p>
						)}
					</div>
				</Skeleton>
			</div>

			{/* 오늘 유입자, 가입자 수 */}
			<div
				className={`dashboard-each-data-div`}
				style={{ marginTop: 0, paddingBottom: 0 }}
			>
				<Skeleton
					loading={loading}
					active={loading}
					size={"large"}
					block={true}
					style={{
						height: "6em",
					}}
					paragraph={{
						rows: 3,
						width: "100%",
					}}
				>
					<div className={`dashboard-huge-number-div`}>
						<div
							className={`compare-price-div`}
							style={{
								flexDirection: "row",
								gap: 5,
							}}
						>
							<div
								className={`compare-price-div`}
								style={{
									flexDirection: "column",
									gap: 5,
									flexGrow: 1,
								}}
							>
								<p>오늘 유입자 수</p>
								<div style={{ display: "flex", alignItems: "end", gap: "2px" }}>
									<h3>{todayValue?.inflow?.toLocaleString()}</h3>
								</div>
							</div>
							<div
								className={`compare-price-div`}
								style={{
									flexDirection: "column",
									gap: 5,
									flexGrow: 1,
								}}
							>
								<p>오늘 가입자 수</p>
								<div style={{ display: "flex", alignItems: "end", gap: "2px" }}>
									<h3>{todayValue?.signup?.toLocaleString()}</h3>
								</div>
							</div>
						</div>
					</div>
				</Skeleton>
			</div>

			{/* 1시간 동안 유저 반응 */}
			<div
				className={`dashboard-each-data-div`}
				style={{ marginTop: 0, paddingBottom: 0 }}
			>
				<div className={`dashboard-huge-number-div`}>
					<Skeleton
						loading={loading}
						active={loading}
						size={"large"}
						block={true}
						style={{
							height: "6em",
						}}
						paragraph={{
							rows: 3,
							width: "100%",
						}}
					>
						<p
							style={{
								fontSize: "1.1em",
								fontWeight: "600",
							}}
						>
							1시간 동안 유저 반응
						</p>
						<p className={`orange`} style={{ marginTop: -3 }}>
							{`${moment()
								.subtract(60, "minutes")
								.format("a h시 m분")} - ${moment().format("a h시 m분")}`}
						</p>
						<div
							style={{
								display: "flex",
								flexDirection: "column",
								gap: 5,
								paddingTop: "0.5em",
							}}
						>
							<div className={`dashboard-data-each`}>
								<b>유입량</b>
								<b>
									{userStreamingList?.filter(
										(item) => item.action === "first_inflow"
									)?.[0]?.count ?? 0}
								</b>
							</div>
							<div className={`dashboard-data-each`}>
								<b>구매 건수</b>
								<b>{purchaseCount}</b>
							</div>
							{userStreamingList
								?.filter((item) => item.action !== "first_inflow")
								?.map((item, idx) => (
									<div
										className={`dashboard-data-each`}
										key={`dashboard-data-${idx}`}
									>
										<p>{item.name}</p>
										<p>{item.count}</p>
									</div>
								))}
						</div>
					</Skeleton>
				</div>
			</div>
			{!loading && (
				<div
					className={`dashboard-each-data-div`}
					style={{
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						gap: "1.5em",
					}}
				>
					<GenderChart genderCount={genderCount} />
					<AgeChart ageList={ageList} />
				</div>
			)}
			<Link to={"/user_manage"}>회원관리로 이동</Link>
		</div>
	);
};

export default DashboardChannel;
